/**
 * Helpers for recovering from a dead Auth0 session (stale/rotated refresh
 * token, revoked session, tenant config change, etc.) without nuking
 * unrelated app state stored under the same origin.
 *
 * Only keys owned by the Auth0 SPA SDK (those prefixed with
 * `@@auth0spajs@@`) are removed — other persisted UI state (e.g.
 * `lextool_current_project_id` in ProjectContext) is preserved.
 */

const AUTH0_STORAGE_PREFIX = '@@auth0spajs@@'
const AUTH0_IDB_NAME = 'auth0spajs'

function clearAuth0KeysFromStorage(storage: Storage) {
  const keysToRemove: string[] = []
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i)
    if (!key) continue
    if (key.startsWith(AUTH0_STORAGE_PREFIX)) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((key) => storage.removeItem(key))
}

/**
 * Purge only Auth0 SDK state (localStorage, sessionStorage, and the
 * `auth0spajs` IndexedDB). Safe to call multiple times; failures in private
 * mode / storage-denied contexts are swallowed.
 */
export function clearAuth0Storage(): void {
  try {
    clearAuth0KeysFromStorage(localStorage)
  } catch {
    // ignore (private mode, quota, etc.)
  }
  try {
    clearAuth0KeysFromStorage(sessionStorage)
  } catch {
    // ignore
  }
  try {
    indexedDB.deleteDatabase(AUTH0_IDB_NAME)
  } catch {
    // ignore
  }
}

/**
 * Module-level guard ensuring we only trigger one `loginWithRedirect` (or
 * equivalent recovery navigation) per page load even when many in-flight
 * API calls simultaneously hit a dead-session error.
 *
 * The flag is intentionally not reset — navigation away from the page
 * resets the module anyway, and any subsequent session-error on the same
 * page would be redundant.
 */
let recoveryTriggered = false

export function tryTriggerSessionRecovery(onRecover: () => void): boolean {
  if (recoveryTriggered) return false
  recoveryTriggered = true
  try {
    onRecover()
  } catch (error) {
    // If the recovery callback throws, allow a later attempt and preserve
    // the original error so failures are diagnosable in logs / error
    // reporting. (`cause` is assigned post-construction because the
    // two-argument `Error` constructor requires TS lib ES2022.)
    recoveryTriggered = false
    const wrapped = new Error('Auth0 session recovery failed')
    ;(wrapped as Error & { cause?: unknown }).cause = error
    throw wrapped
  }
  return true
}
